/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { Eyebrow, getColorFromColorName, unifiedModuleColors } from '@coursera-web/cds-labs';
import { cloneDeep } from 'lodash';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useLocation } from 'js/lib/useRouter';

import { Grid, Typography2, breakpoints } from '@coursera/cds-core';

import SimpleRichTextContentful from 'bundles/coursera-plus-landing-page/components/SimpleRichTextContentful';
import CTAContentful from 'bundles/coursera-plus-landing-page/components/ctaContentful/CTAContentful';
import { CTA_LOCATIONS } from 'bundles/coursera-plus-landing-page/contentful/constants';
import { useLandingPageExtended } from 'bundles/coursera-plus-landing-page/contentfulData/useLandingPageData';
import { logo } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import { getShouldShowXDPAndCourseraPlusLandingPageChanges } from 'bundles/coursera-plus/utils/subscriptionsForwardDiscoveryExperiment';

import HeroImage from 'images/coursera-plus-landing-page/hero_image_woman_looking_at_tablet.png';

import _t from 'i18n!nls/coursera-plus-landing-page';

const HEADER_COMPONENT = 'h1';
const MIN_HERO_HEIGHT = 400;

const styles = {
  outer: css`
    background-color: ${getColorFromColorName(unifiedModuleColors.lightBlue)};
    min-height: ${MIN_HERO_HEIGHT}px;
  `,
  inner: css`
    margin-left: auto;
    margin-right: auto;
    max-width: ${breakpoints.values.lg}px;
    padding: 0 var(--cds-spacing-600);

    ${breakpoints.down('sm')} {
      padding: 0 var(--cds-spacing-200);
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-300) var(--cds-spacing-200);
    }
  `,
  heading: css`
    margin-bottom: var(--cds-spacing-150);
  `,
  imageContainer: css`
    display: flex;
    flex-direction: row;

    ${breakpoints.up('lg')} {
      justify-content: right;
    }

    ${breakpoints.down('xs')} {
      justify-content: center;
      margin-top: var(--cds-spacing-150);
    }
  `,
  image: (shouldClipImage: boolean = true) => css`
    max-height: 400px;

    ${breakpoints.up('sm')} {
      ${shouldClipImage &&
      `
      --bottom-clip: 40px;

      clip-path: inset(0 0 var(--bottom-clip) 0);
      position: relative;
      bottom: calc(-1 * var(--bottom-clip));
      `}
    }

    ${breakpoints.down('xs')} {
      max-width: 100%;
      max-height: 250px;
    }
  `,
  description: css`
    display: flex;
    flex-direction: column;
    justify-content: center;

    ${breakpoints.up('sm')} {
      padding: 0 var(--cds-spacing-300) 0 0;
    }
    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-200) var(--cds-spacing-300);

      ${HEADER_COMPONENT} {
        margin-bottom: var(--cds-spacing-100);
      }
    }
  `,
  ctaContainer: (userHasPlus: boolean) => css`
    button {
      margin-top: ${userHasPlus ? `var(--cds-spacing-200)` : `var(--cds-spacing-100)`};
      margin-bottom: 0;

      ${breakpoints.down('xs')} {
        margin-top: var(--cds-spacing-300);
      }
    }

    // this block and the style below prevent the yearly price link
    // from creating too much space below itself
    button:last-child {
      padding-bottom: 0;
    }

    div:has(> button) {
      margin-bottom: 0 !important;
    }
  `,

  /*
    Some adjustments for the price presentation variant, e.g lighter background and a larger image
  */
  pricePresentationOuter: css`
    background-color: (var(--cds-color-white-0));
    border-bottom: 1px solid var(--cds-color-neutral-background-primary-weak);
  `,

  pricePresentationCtaContainer: css`
    margin: var(--cds-spacing-300) 0;
    z-index: 10; /* Ensure the Price CTAs overlay the image */
  `,

  pricePresentationImage: css`
    /* Make the image slightly larger for the price presentation variant */
    align-self: flex-end;
    max-height: 450px;
  `,

  pricePresentationDescription: css`
    ${breakpoints.up('sm')} {
      padding: var(--cds-spacing-400) var(--cds-spacing-300) var(--cds-spacing-200) 0;
    }
  `,
};

const CourseraPlusHeroModule = () => {
  const pageData = useLandingPageExtended();
  const { pathname } = useLocation();
  const {
    userHasCourseraPlusFreeTrial,
    isCourseraPlusOwner,
    isCourseraLiteOwner,
    courseraPlusAnnualWithFreeTrialEpicVariant,
  } = pageData;
  const userHasPlus = [userHasCourseraPlusFreeTrial, isCourseraPlusOwner].includes(true);
  const { heroTextMedium2, heroCtaPartsCollection, heroImage } = pageData.landingPage;
  const ctaParts = heroCtaPartsCollection?.items?.filter(Boolean) ?? [];
  const partsToCta = cloneDeep(ctaParts);
  partsToCta[0].displayAsBold = false;
  const isCPlusOrCLiteOwnerOnPromoPage =
    (isCourseraPlusOwner || isCourseraLiteOwner) && pathname.includes('/courseraplus/special');

  let heroTextContent: JSX.Element | null | undefined = heroTextMedium2 && (
    <SimpleRichTextContentful richText={heroTextMedium2} />
  );

  const isPricePresentationVariant =
    courseraPlusAnnualWithFreeTrialEpicVariant === '2' || courseraPlusAnnualWithFreeTrialEpicVariant === '3';

  if (isCPlusOrCLiteOwnerOnPromoPage) {
    // On promo pages, we do not set this text on the Contentful side
    // for Coursera Plus owners, so we have to hard code it here.
    heroTextContent = (
      <FormattedMessage
        message={_t('Achieve your career goals with {productTypeName}')}
        productTypeName={isCourseraLiteOwner ? _t('Coursera Lite') : _t('Coursera Plus')}
      />
    );
  }

  const shouldShowHardCodedHeroImage = getShouldShowXDPAndCourseraPlusLandingPageChanges();

  let heroImageUrl;
  if (shouldShowHardCodedHeroImage) {
    heroImageUrl = HeroImage;
  } else {
    heroImageUrl = heroImage?.url;
  }

  return (
    <div css={[styles.outer, isPricePresentationVariant && styles.pricePresentationOuter]}>
      <Grid container css={styles.inner}>
        <Grid
          item
          xs={12}
          sm={isPricePresentationVariant ? 9 : 6}
          md={isPricePresentationVariant ? 7 : 6}
          lg={6}
          css={[styles.description, isPricePresentationVariant && styles.pricePresentationDescription]}
        >
          <Eyebrow image={{ url: logo.PLUS_FULL_BLUE, height: 20 }} />
          {heroTextContent && (
            <Typography2 component={HEADER_COMPONENT} variant="titleLarge" css={styles.heading}>
              {heroTextContent}
            </Typography2>
          )}
          <div
            css={isPricePresentationVariant ? styles.pricePresentationCtaContainer : styles.ctaContainer(userHasPlus)}
          >
            <CTAContentful ctaPieces={partsToCta} ctaLocation={CTA_LOCATIONS.hero} />
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          sm={isPricePresentationVariant ? 3 : 6}
          md={isPricePresentationVariant ? 5 : 6}
          lg={6}
          css={styles.imageContainer}
        >
          {heroImageUrl && (
            <Imgix
              src={heroImageUrl}
              alt=""
              css={[
                styles.image(!shouldShowHardCodedHeroImage),
                isPricePresentationVariant && styles.pricePresentationImage,
              ]}
            />
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default CourseraPlusHeroModule;
